(function (root, factory) {
  const api = factory(root);
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  root.JudesProbierMeasurement = api;
}(typeof globalThis !== 'undefined' ? globalThis : this, function (root) {
  'use strict';
  // Reporting boundary: 2026-09-24 10:00:00 UTC (12:00 Europe/Berlin).
  const EXPERIMENT = 'probier_pdp_ab_20260924_offer_top';
  const ENDPOINT = 'https://judes.sfrance.co/experiments/probier/events';
  const TTL = 30 * 86400000;
  const EVENTS = ['exposure', 'builder_start', 'summary', 'cart_success', 'checkout_start'];
  const DELAYS = [1000, 2000, 4000, 8000, 30000, 60000, 300000];
  const MAX_ATTEMPTS = 8;

  function create(config) {
    // Cross-tab initialization and every shared-state mutation use one origin lock.
    // Unsupported/denied storage is explicitly unavailable, never counted as delivered.
    if (!root.navigator?.locks?.request) return null;
    const lock = `${EXPERIMENT}:measurement:${config?.qa ? `qa:${config.arm}` : 'production'}`;
    const exclusive = fn => root.navigator.locks.request(lock, fn);
    const ready = exclusive(() => createLocked(config, exclusive));
    return Object.freeze({ track(event) {
      if (!EVENTS.includes(event)) return false;
      void ready.then(api => api?.track(event)).catch(() => {});
      return true; // Queued initialization, never a server acknowledgment.
    }});
  }

  function createLocked(config, exclusive) {
    if (config?.endpoint !== ENDPOINT || !['control', 'variant'].includes(config.arm)
      || typeof config.qa !== 'boolean' || !Number.isSafeInteger(config.expiresAt)
      || config.expiresAt <= Date.now() || config.expiresAt > Date.now() + TTL
      || typeof root.fetch !== 'function' || !root.crypto?.getRandomValues) return null;
    const key = `${EXPERIMENT}:measurement:${config.qa ? `qa:${config.arm}` : 'production'}`;
    let state;
    let timer;
    const inFlight = new Set();
    const valid = value => value && /^[a-f0-9]{32}$/.test(value.browser_id)
      && value.arm === config.arm && value.qa === config.qa && value.expiresAt === config.expiresAt
      && value.pending && typeof value.pending === 'object' && value.delivered && typeof value.delivered === 'object'
      && Object.entries(value.delivered).every(([event, flag]) => EVENTS.includes(event) && flag === true)
      && Object.entries(value.pending).every(([event, item]) => EVENTS.includes(event) && item
        && Number.isInteger(item.attempts) && item.attempts >= 0 && item.attempts <= MAX_ATTEMPTS
        && Number.isSafeInteger(item.nextAt) && item.nextAt >= 0 && item.nextAt <= config.expiresAt + 300000);
    function read() {
      try {
        if (Date.now() >= config.expiresAt) {
          const old = JSON.parse(root.localStorage.getItem(key));
          if (old?.expiresAt === config.expiresAt) root.localStorage.removeItem(key);
          return null;
        }
        const value = JSON.parse(root.localStorage.getItem(key));
        return valid(value) ? value : null;
      } catch (_) { return null; }
    }
    function write(value) {
      try { root.localStorage.setItem(key, JSON.stringify(value)); return true; } catch (_) { return false; }
    }
    try {
      const prior = JSON.parse(root.localStorage.getItem(key));
      if (valid(prior)) state = prior;
      else {
        // Never replace a still-live identity on a conflicting configuration.
        if (prior?.expiresAt > Date.now()) return null;
        const bytes = new Uint32Array(4);
        root.crypto.getRandomValues(bytes);
        state = { browser_id: Array.from(bytes, n => n.toString(16).padStart(8, '0')).join(''),
          arm: config.arm, qa: config.qa, expiresAt: config.expiresAt, pending: {}, delivered: {} };
        if (!write(state)) return null;
      }
    } catch (_) { return null; }
    const browserId = state.browser_id;
    function current() {
      const value = read();
      return value?.browser_id === browserId ? value : null;
    }
    function schedule() {
      root.clearTimeout(timer);
      const value = current();
      if (!value) return;
      const times = EVENTS.filter(event => value.pending[event] && !inFlight.has(event)
        && value.pending[event].attempts < MAX_ATTEMPTS).map(event => value.pending[event].nextAt);
      // Also remove our local identifier and pending records at the fixed expiry.
      const next = Math.min(config.expiresAt, ...times);
      timer = root.setTimeout(flush, Math.min(2147483647, Math.max(0, next - Date.now())));
    }
    async function send(event) {
      if (inFlight.has(event)) return;
      inFlight.add(event);
      const started = await exclusive(() => {
        const value = current();
        const pending = value?.pending[event];
        if (!pending || pending.attempts >= MAX_ATTEMPTS) return false;
        pending.attempts += 1;
        pending.nextAt = Date.now() + (DELAYS[pending.attempts - 1] || 300000);
        return write(value);
      });
      if (!started) { inFlight.delete(event); return; }
      let timeout;
      const controller = typeof root.AbortController === 'function' ? new root.AbortController() : null;
      try {
        const request = root.fetch(ENDPOINT, { method: 'POST', mode: 'cors', credentials: 'omit',
          referrerPolicy: 'no-referrer', cache: 'no-store', keepalive: true,
          ...(controller ? { signal: controller.signal } : {}),
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ experiment_id: EXPERIMENT, browser_id: browserId,
            arm: config.arm, event, qa: config.qa })
        }).then(async response => response.ok && (await response.json())?.accepted === true);
        const accepted = await Promise.race([request, new Promise(resolve => {
          timeout = root.setTimeout(() => { controller?.abort(); resolve(false); }, 8000);
        })]);
        if (accepted) {
          await exclusive(() => {
            const latest = current();
            if (latest) { latest.delivered[event] = true; delete latest.pending[event]; write(latest); }
          });
        }
      } catch (_) { /* Network/JSON/server failures remain pending; never synthesize receipt. */ }
      finally { root.clearTimeout(timeout); inFlight.delete(event); schedule(); }
    }
    function flush(force = false) {
      const value = current();
      if (!value) return;
      for (const event of EVENTS) {
        if (value.pending[event] && (force === true || value.pending[event].nextAt <= Date.now())) void send(event);
      }
      schedule();
    }
    async function track(event) {
      if (!EVENTS.includes(event)) return false;
      const queued = await exclusive(() => {
        const value = current();
        if (!value || value.delivered[event] === true) return false;
        if (!value.pending[event]) {
          value.pending[event] = { attempts: 0, nextAt: Date.now() };
          if (!write(value)) return false;
        }
        return true;
      });
      if (queued) flush();
      return queued;
    }
    root.addEventListener?.('pagehide', () => flush(true));
    root.addEventListener?.('online', () => flush());
    root.document?.addEventListener?.('visibilitychange', () => {
      if (root.document.visibilityState === 'hidden') flush(true);
    });
    flush();
    return Object.freeze({ track });
  }
  return Object.freeze({ create });
}));
